/*
  ConfidenceBadge — tiny amber/green/red pill showing how much we trust a
  course's data. Backend computes the tier in `/api/courses/lite` +
  `/api/courses/{id}`; this component just renders it.

  Two sizes:
    - `size="sm"` (default) — pill for course row cards.
    - `size="md"` — bigger chip for the course detail header, with the
      human explanation right underneath.

  Falls back to `null` if the backend didn't include a `confidence` block
  (e.g. an old cached lite response) so we never break the list.
*/
import { CheckCircle, Warning, WarningOctagon } from "@phosphor-icons/react";

const TIERS = {
  green: {
    bg: "rgba(16,185,129,.14)",
    fg: "#0F9C6C",
    icon: CheckCircle,
    tip: "Fully verified — GPS pins covered and scorecard confirmed.",
  },
  amber: {
    bg: "rgba(217,119,6,.14)",
    fg: "#B45309",
    icon: Warning,
    tip: "Partial — usable but help verify by confirming a pin during your round.",
  },
  red: {
    bg: "rgba(196,84,60,.14)",
    fg: "#B04A34",
    icon: WarningOctagon,
    tip: "Unverified — double-check yardages and pin positions before playing.",
  },
};

export default function ConfidenceBadge({ confidence, size = "sm", showTip = false }) {
  if (!confidence?.tier || !TIERS[confidence.tier]) return null;
  const t = TIERS[confidence.tier];
  const Icon = t.icon;
  const isMd = size === "md";
  return (
    <div className={isMd ? "space-y-1" : ""} data-testid={`confidence-badge-${confidence.tier}`}>
      <span
        className={`inline-flex items-center gap-1 rounded-full font-heading font-bold ${isMd ? "text-xs px-2.5 py-1" : "text-[10px] px-2 py-0.5"}`}
        style={{ background: t.bg, color: t.fg }}
        title={t.tip}
      >
        <Icon size={isMd ? 12 : 10} weight="fill" />
        <span className="uppercase tracking-wider">{confidence.label || confidence.tier}</span>
      </span>
      {showTip && (
        <p className="text-[11px] text-[var(--text-secondary)] leading-snug" data-testid="confidence-tip">
          {t.tip}
          {confidence.total_holes > 0 && (
            <>
              {" "}
              <span className="font-mono-num">
                {confidence.gps_holes}/{confidence.total_holes}
              </span>{" "}
              holes have GPS pins
              {confidence.confirmed_holes > 0 && (
                <>
                  {", "}
                  <span className="font-mono-num">{confidence.confirmed_holes}</span> user-confirmed
                </>
              )}
              {confidence.scorecard_verified && " · scorecard verified"}
              .
            </>
          )}
        </p>
      )}
    </div>
  );
}
